import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { SiteHeader } from "../components/SiteHeader.tsx";
import { SiteFooter } from "../components/SiteFooter.tsx";
import { api, ApiError, type ConsultationPage } from "../lib/api.ts";
import { STUDIO } from "../lib/studio.ts";
import {
  answeredCount,
  ConsultationFields,
  formatAnswer,
  initialAnswers,
  isContentKind,
  toSubmission,
  type AnswerMap,
} from "../consultation/ConsultationFields.tsx";
import "./book.css";
import "./consultation.css";

/**
 * The client's consultation form.
 *
 * Reached from the link in their confirmation email, which carries the same
 * reference and token as their booking page. There is no login, because there
 * are no accounts - the token in the link is the credential.
 *
 * A form already sent can be edited rather than only viewed: somebody who
 * remembers a medication two days later must have somewhere to put it, and the
 * alternative is that they do not mention it at all.
 */
export function Consultation() {
  const { reference = "" } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") ?? "";

  const [page, setPage] = useState<ConsultationPage | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Set once the form has been sent in this visit, so the page can lead with
  // thanks rather than with the questions again.
  const [justSent, setJustSent] = useState(false);
  const [editing, setEditing] = useState(false);
  const errorSummary = useRef<HTMLDivElement>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      try {
        const result = await api.getConsultation(reference, token, signal);
        setPage(result);
        setAnswers(
          initialAnswers(result.form.fields, result.submission?.answers),
        );
      } catch (caught) {
        if ((caught as Error)?.name === "AbortError") return;
        setError(
          caught instanceof ApiError
            ? caught.message
            : "That form could not be loaded.",
        );
      }
    },
    [reference, token],
  );

  useEffect(() => {
    if (!token) {
      setError(
        "This link is incomplete. Please use the full link from your email.",
      );
      return;
    }
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, token]);

  const questions = useMemo(
    () =>
      (page?.form.fields ?? []).filter((field) => !isContentKind(field.kind)),
    [page],
  );
  const answered = page ? answeredCount(page.form.fields, answers) : 0;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!page) return;

    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const result = await api.submitConsultation(
        reference,
        token,
        toSubmission(page.form.fields, answers),
      );
      setPage(result);
      setAnswers(
        initialAnswers(result.form.fields, result.submission?.answers),
      );
      setMessage(
        result.message ?? "Thank you - your form is with your therapist.",
      );
      setJustSent(true);
      setEditing(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (caught) {
      if (caught instanceof ApiError) {
        const perField = caught.fieldErrors();
        if (perField.length > 0) {
          setFieldErrors(
            Object.fromEntries(
              perField.map((entry) => [entry.fieldId, entry.message]),
            ),
          );
        }
        setError(caught.message);
        // Move to the summary rather than leaving them at the bottom of a long
        // form wondering why the button did nothing.
        requestAnimationFrame(() =>
          errorSummary.current?.scrollIntoView({ block: "center" }),
        );
      } else {
        setError("That could not be sent. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (error && !page) {
    return (
      <>
        <SiteHeader variant="inner" />
        <main className="booking">
          <div className="page">
            <div className="confirmation">
              <h1 className="booking__title">We could not open that form</h1>
              <p className="notice notice--error">{error}</p>
              <p className="booking__hint">
                If your link has gone astray, ring the studio on{" "}
                <a href={`tel:${STUDIO.phoneHref}`}>{STUDIO.phoneDisplay}</a>{" "}
                and we will send it again - or simply fill the form in when you
                arrive.
              </p>
              <div className="confirmation__actions">
                <Link to="/" className="btn btn--ghost">
                  Back to the website
                </Link>
              </div>
            </div>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  if (!page) {
    return (
      <>
        <SiteHeader variant="inner" />
        <main className="booking">
          <div className="page">
            <p className="booking__loading" aria-live="polite">
              <span className="spinner" /> Loading your form…
            </p>
          </div>
        </main>
        <SiteFooter />
      </>
    );
  }

  const alreadySent = page.submission !== null;
  // Somebody arriving back at a completed form sees what they said, with the
  // option to change it - not a blank form implying nothing was received.
  const showingSummary = alreadySent && !editing;
  const noQuestions = questions.length === 0;

  return (
    <>
      <SiteHeader variant="inner" />
      <main className="booking">
        <div className="page cform">
          <p className="eyebrow">{page.booking.serviceName}</p>
          <h1 className="booking__title">{page.form.title}</h1>

          <p className="cform__appointment">
            {page.booking.dateLabel} at{" "}
            <strong>{page.booking.timeLabel}</strong>
            <span aria-hidden="true"> · </span>
            <span className="cform__reference">{page.booking.reference}</span>
          </p>

          {message && (
            <p className="notice notice--success" aria-live="polite">
              {message}
            </p>
          )}

          {!page.editable && page.closedReason && (
            <p className="notice">{page.closedReason}</p>
          )}

          {noQuestions && page.editable && (
            <p className="notice">
              There is nothing to fill in at the moment. Nothing for you to do -
              we will go through everything when you arrive.
            </p>
          )}

          {page.form.intro && !showingSummary && !noQuestions && (
            <p className="cform__intro lede">{page.form.intro}</p>
          )}

          {showingSummary ? (
            <section className="panel cform__summary stack-lg">
              <div>
                <h2 className="cform__summary-title">
                  {justSent ? "That is everything, thank you" : "Your answers"}
                </h2>
                <p className="field-hint">
                  Sent{" "}
                  {new Date(page.submission!.submittedUtc).toLocaleDateString(
                    "en-GB",
                    {
                      day: "numeric",
                      month: "long",
                    },
                  )}
                  {page.submission!.updatedUtc && ", and updated since"}. Only
                  your therapist sees this.
                </p>
              </div>

              <dl className="answer-list">
                {page.form.fields
                  .filter((field) => !isContentKind(field.kind))
                  .map((field) => {
                    const values = answers[field.id] ?? [];
                    if (values.length === 0) return null;
                    const consent = field.kind === "consent";
                    return (
                      <div
                        key={field.id}
                        className={consent ? "answer-list__consent" : undefined}
                      >
                        <dt>{field.label}</dt>
                        <dd>
                          {consent ? (
                            <span className="answer-list__agreed">Agreed</span>
                          ) : (
                            values.map((value) => (
                              <span key={value} className="cform__answer-line">
                                {formatAnswer(field.kind, value)}
                              </span>
                            ))
                          )}
                        </dd>
                      </div>
                    );
                  })}
              </dl>

              {page.editable && (
                <div className="row">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => {
                      setEditing(true);
                      setMessage(null);
                      setJustSent(false);
                    }}
                  >
                    Change an answer
                  </button>
                </div>
              )}
            </section>
          ) : (
            page.editable &&
            !noQuestions && (
              <form
                className="cform__form stack-lg"
                onSubmit={submit}
                noValidate
              >
                <div ref={errorSummary}>
                  {error && (
                    <p className="notice notice--error" aria-live="assertive">
                      {error}
                    </p>
                  )}
                </div>

                <ConsultationFields
                  fields={page.form.fields}
                  answers={answers}
                  errors={fieldErrors}
                  disabled={saving}
                  onChange={(fieldId, values) => {
                    setAnswers((current) => ({
                      ...current,
                      [fieldId]: values,
                    }));
                    // Clear the complaint as soon as they act on it.
                    setFieldErrors((current) => {
                      if (!(fieldId in current)) return current;
                      const next = { ...current };
                      delete next[fieldId];
                      return next;
                    });
                  }}
                />

                <div className="cform__actions">
                  <p className="field-hint" aria-live="polite">
                    {answered} of {questions.length} answered
                  </p>
                  <button type="submit" className="btn" disabled={saving}>
                    {saving ? (
                      <>
                        <span className="spinner" /> Sending…
                      </>
                    ) : alreadySent ? (
                      "Save changes"
                    ) : (
                      "Send my form"
                    )}
                  </button>
                  {alreadySent && (
                    <button
                      type="button"
                      className="btn btn--quiet"
                      disabled={saving}
                      onClick={() => {
                        setEditing(false);
                        setFieldErrors({});
                        setError(null);
                        setAnswers(
                          initialAnswers(
                            page.form.fields,
                            page.submission?.answers,
                          ),
                        );
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            )
          )}

          <p className="cform__footnote">
            Everything here is seen only by your therapist and is kept with your
            appointment. If you would rather go through it in person, that is
            entirely fine - call the studio on{" "}
            <a href={`tel:${STUDIO.phoneHref}`}>{STUDIO.phoneDisplay}</a>.
          </p>

          <div className="confirmation__actions">
            <Link
              to={`/booking/${page.booking.reference}?t=${encodeURIComponent(token)}`}
              className="btn btn--ghost"
            >
              Back to your booking
            </Link>
          </div>
        </div>
      </main>
      <SiteFooter />
    </>
  );
}
